import { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useListCountries } from 'hooks/useListCountries';
import { createCountry, getAvailableCountries, updateCountryData } from 'services/countriesList';
import { CountryDefaultCurrencyRole, ICountryDetail, INewCountryForm, IUpdateCountry } from 'types/countries';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { TAny } from 'types/blog';
import { createNewProvider } from 'services/orders';
import { CountryRow, CountryRowForm, CountriesHeader, AddCountryButton, AddCountryForm, LoaderRow } from './components';
import './countriesTab.scss';

const INITIAL_LOADER_ROW_COUNT = 10;
const NO_COUNTRY_UPDATING_ID = 0;

/**
 * Tab de gestión de países: listado, creación, edición y proveedores.
 */
const CountriesTab = () => {
  const countriesTableBodyRef = useRef<HTMLTableSectionElement>(null);
  const initialLoaderRowIndexes = Array.from({ length: INITIAL_LOADER_ROW_COUNT }, (_, index) => index);

  const { countries: registeredCountries = [], areCountriesLoading, mutateCountriesList } = useListCountries();

  const [isAddCountryFormOpen, setIsAddCountryFormOpen] = useState<boolean>(false);
  const [pendingCountryFormData, setPendingCountryFormData] = useState<INewCountryForm>();
  const [stagedCountry, setStagedCountry] = useState<ICountryDetail>();
  const [updatingCountryId, setUpdatingCountryId] = useState<number>(NO_COUNTRY_UPDATING_ID);
  const [isSavingStagedCountry, setIsSavingStagedCountry] = useState<boolean>(false);
  const [availableCountriesCatalog, setAvailableCountriesCatalog] = useState<TAny>();

  /**
   * Desplaza el listado al inicio para mostrar el país recién preparado.
   */
  const scrollToCountryListStart = () => {
    countriesTableBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Revalida el listado de países y reinicia el estado del formulario de creación.
   */
  const refreshCountries = async () => {
    await mutateCountriesList();
    setPendingCountryFormData(undefined);
    setStagedCountry(undefined);
  };

  /**
   * Actualiza la información general de un país existente.
   */
  const handleUpdateCountry = async (data: IUpdateCountry, countryId: number) => {
    setUpdatingCountryId(countryId);

    try {
      const message = await updateCountryData(data, countryId);
      await refreshCountries();
      setUpdatingCountryId(NO_COUNTRY_UPDATING_ID);
      displaySuccessNotification({ customJSXMessage: <>{message}</> });
    } catch (error) {
      displayErrorNotification();
      setUpdatingCountryId(NO_COUNTRY_UPDATING_ID);
    }
  };

  /**
   * Persiste en backend el país previamente preparado en el flujo de creación.
   */
  const handleSaveStagedCountry = async (data: IUpdateCountry) => {
    setIsSavingStagedCountry(true);

    const countryPayload = {
      code: pendingCountryFormData?.ISOCode,
      currency: pendingCountryFormData?.currencyType?.toLocaleUpperCase(),
      exchangeRate: data.exchangeRate,
      taxRate: data.taxRate,
      phoneCode: data.phoneCode,
      documentType: pendingCountryFormData?.documentName?.toLocaleUpperCase(),
      documentExample: data.documentExample,
      defaultCurrencyRole: data.defaultCurrencyRole,
      registry: data.registry,
      buy: data.buy,
      logisticService: data.logisticService,
      storage: data?.storage
    };

    const formData = new FormData();
    const flagExtension = pendingCountryFormData?.flag.name
      .substring(pendingCountryFormData.flag.name.lastIndexOf('.'))
      .toLowerCase();

    if (pendingCountryFormData?.flag) {
      formData.append('file', pendingCountryFormData.flag, `country-img-${uuid()}.${flagExtension}`);
    }

    formData.append('data', JSON.stringify(countryPayload));

    try {
      await createCountry(formData);
      displaySuccessNotification({
        customJSXMessage: <>El país quedo creado, termina de llenar la información para activarlo.</>
      });
      await refreshCountries();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsSavingStagedCountry(false);
    }
  };

  /**
   * Registra un proveedor nuevo asociado a un país.
   */
  const handleAddProvider = async (provider: string, countryCode: string, countryId: number) => {
    setUpdatingCountryId(countryId);

    try {
      const providerPayload = {
        name: provider,
        countryId,
        countryCode
      };

      await createNewProvider(providerPayload);
      await refreshCountries();
      displaySuccessNotification({ customJSXMessage: <>Se guardó la información correctamente.</> });
      setUpdatingCountryId(NO_COUNTRY_UPDATING_ID);
    } catch (error) {
      displayErrorNotification();
      setUpdatingCountryId(NO_COUNTRY_UPDATING_ID);
    }
  };

  /**
   * Prepara la vista previa del país seleccionado antes de guardarlo en backend.
   */
  const buildStagedCountryPreview = (countryFormData: INewCountryForm) => {
    const flagPreviewUrl: string = URL.createObjectURL(countryFormData.flag);
    const normalizedCurrencyCode = countryFormData.currencyType?.toUpperCase();
    const hasUsdAsPrimaryCurrency = normalizedCurrencyCode === 'USD';

    setStagedCountry({
      name: countryFormData.name,
      documentType: countryFormData.documentName,
      currencyCode: normalizedCurrencyCode,
      exchangeRate: countryFormData.currencyExchangeRate?.toString(),
      secondaryCurrencyCode: hasUsdAsPrimaryCurrency ? undefined : 'USD',
      defaultCurrencyRole: CountryDefaultCurrencyRole.PRIMARY,
      flag: flagPreviewUrl,
      code: countryFormData.ISOCode
    });
    setIsAddCountryFormOpen(false);
    scrollToCountryListStart();
  };

  /**
   * Confirma la selección del formulario de nuevo país y lo muestra como borrador.
   */
  const handleCountryFormSubmit = (countryFormData: INewCountryForm) => {
    buildStagedCountryPreview(countryFormData);
    setPendingCountryFormData(countryFormData);
  };

  /**
   * Carga el catálogo de países disponibles para crear.
   */
  const fetchAvailableCountries = async () => {
    try {
      const availableCountries = await getAvailableCountries();
      setAvailableCountriesCatalog(availableCountries);
    } catch (error) {
      displayErrorNotification();
    }
  };

  /**
   * Abre el formulario para seleccionar un país nuevo del catálogo.
   */
  const handleOpenAddCountryForm = () => {
    setIsAddCountryFormOpen(true);
  };

  /**
   * Cierra el formulario de selección de país nuevo.
   */
  const handleCloseAddCountryForm = () => {
    setIsAddCountryFormOpen(false);
  };

  const hasStagedCountry = !!stagedCountry;
  const shouldShowStagedCountryRowLoader = hasStagedCountry && isSavingStagedCountry;
  const shouldShowStagedCountryRow = hasStagedCountry && !isSavingStagedCountry;
  const shouldShowInitialLoaderRows = areCountriesLoading;
  const hasRegisteredCountries = registeredCountries.length > 0;
  const shouldShowAddCountryForm = isAddCountryFormOpen && !!availableCountriesCatalog;
  const shouldShowAddCountryButton = !shouldShowAddCountryForm;
  const isAddCountryButtonDisabled = hasStagedCountry || areCountriesLoading;

  /**
   * Renderiza el contenido del cuerpo de la tabla de países.
   */
  const renderTableBodyContent = () => {
    if (shouldShowInitialLoaderRows) {
      return initialLoaderRowIndexes.map(loaderIndex => <LoaderRow key={`country-initial-loader-${loaderIndex}`} />);
    }

    return (
      <>
        {shouldShowStagedCountryRowLoader && <LoaderRow />}

        {shouldShowStagedCountryRow && stagedCountry && (
          <CountryRowForm countryData={stagedCountry} onSave={handleSaveStagedCountry} />
        )}

        {hasRegisteredCountries &&
          registeredCountries.map(country => (
            <CountryRow
              updateLoading={updatingCountryId}
              key={country.id}
              countryData={country}
              handleupdateCountry={handleUpdateCountry}
              handleNewProvider={handleAddProvider}
            />
          ))}
      </>
    );
  };

  useEffect(() => {
    fetchAvailableCountries();
  }, []);

  return (
    <div className="countriesTab">
      <div className="countriesTabScroll">
        <table className="countriesTabTable">
          <CountriesHeader />
          <tbody ref={countriesTableBodyRef}>{renderTableBodyContent()}</tbody>
        </table>
      </div>

      <div className="countriesTabFooter">
        {shouldShowAddCountryForm && availableCountriesCatalog && (
          <AddCountryForm
            registerList={registeredCountries}
            availableList={availableCountriesCatalog}
            className="mt-16"
            onSubmit={handleCountryFormSubmit}
            onClose={handleCloseAddCountryForm}
          />
        )}

        {shouldShowAddCountryButton && (
          <AddCountryButton
            disabled={isAddCountryButtonDisabled}
            className="mt-16"
            onClick={handleOpenAddCountryForm}
          />
        )}
      </div>
    </div>
  );
};

export default CountriesTab;
